import React, { useCallback, useEffect, useState } from 'react'
import {
	Box,
	Grid,
	Paper,
	Skeleton,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import { BarChart }  from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { MY_BUSINESS_ROUTES } from '@constants/routes'
import { AppCardTitle, AppEmptyLine, AppPillToggle, AppNote } from '@components'
import { displayDay } from '@utils'
import { dsColorNew, dataColors } from '@palette'
import { MbPage, MbKpiCard, MbKpiRow, MB_CHART_COLORS, MbPageSkeleton, mbSectionBarSx, mbLegendBottom } from 'Pages/MyBusiness/_shared/Ui'

import {
	FiltersBar,
	defaultFilters,
	filtersToQuery,
} from 'Pages/MyBusiness/_shared/Filters'
import { ShareList, PeakCalendar, FleetCascadeMatrix, buildTickFormatter, pickTickNumber } from 'Pages/MyBusiness/_shared/Charts'

/**
 * A3 — Fleet.
 *
 * Fleet size + utilization at a glance: KPI banner (Avg Fleet, Avg
 * Booked, Avg Available, Utilization %) + monthly chart pivotable to
 * any of those metrics. Inherits the project-wide filter baseline
 * (date presets, Office/Category slicers, PY/custom comparison) from
 * the Revenue dashboard.
 *
 * Backend: GET /api/my-business/fleet?from=&to=&offices=&sipps=&
 *                                     compare=&compare_from=&compare_to=&
 *                                     granularity=
 *
 * Next sprints: Peak Calendar (day × office × SIPP), idle reasons
 * breakdown, fleet-by-category share.
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)
const fmtPct = (n) =>
	new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
		.format(n) + ' %'

const METRICS = {
	avg_fleet:     { key: 'avg_fleet',     label: 'Avg Fleet',     format: fmtNum, chartTitle: 'Avg Fleet' },
	avg_booked:    { key: 'avg_booked',    label: 'Avg Booked',    format: fmtNum, chartTitle: 'Avg Booked' },
	avg_available: { key: 'avg_available', label: 'Avg Available', format: fmtNum, chartTitle: 'Avg Available' },
	utilization:   { key: 'utilization',   label: 'Utilization',   format: fmtPct, chartTitle: 'Utilization' },
}

const GRANULARITY_LABEL = {
	daily:   'Daily',
	weekly:  'Weekly',
	monthly: 'Monthly',
}
const GRANULARITY_OPTIONS = Object.entries(GRANULARITY_LABEL).map(([value, label]) => ({ value, label }))

export default function MyBusinessFleetPage() {
	const [applied, setApplied]     = useState(defaultFilters)
	const [draft,   setDraft]       = useState(defaultFilters)
	const [data,    setData]        = useState(null)
	const [error,   setError]       = useState(null)
	// A granularity switch only reloads the chart: the rest of the page stays
	const [chartReloading, setChartReloading] = useState(false)
	const [loading, setLoading]     = useState(true)
	const [activeMetricKey, setActiveMetricKey] = useState('utilization')
	const [granularity, setGranularity] = useState('auto')
	// Breakdown view mode (Thibault's "Fleet by Category" 3 checkboxes):
	//   'raw'      → absolute values, no comparison
	//   'vs_py'    → absolute + delta vs PY  (default when compare=py)
	//   'per_unit' → divided by avg_fleet (current) and avg_fleet_py
	const [viewMode, setViewMode] = useState('vs_py')
	const activeMetric = METRICS[activeMetricKey] || METRICS.utilization

	const fetchFleet = useCallback((f, g) => {
		setLoading(true); setError(null)
		let url = `/api/my-business/fleet?${filtersToQuery(f)}`
		if (g && g !== 'auto') url += `&granularity=${g}`
		fetch(url, { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => { setLoading(false); setChartReloading(false) })
	}, [])

	const handleApply = useCallback((next) => {
		setApplied(next)
		setDraft(next)
		setGranularity('auto')
		fetchFleet(next, 'auto')
	}, [fetchFleet])

	const handleGranularity = (g) => {
		setChartReloading(true)
		setGranularity(g)
		fetchFleet(applied, g)
	}

	const pageLoading = loading && !chartReloading

	useEffect(() => { fetchFleet(applied, 'auto') }, [])  // initial only

	// Cross-filter: reflect a 1-office or 1-category "isolate" applied
	// state in the corresponding ShareList highlight. Mirrors Revenue.
	const selectedOffice =
		applied.offices.length === 1 ? applied.offices[0] : null
	const selectedCategory =
		applied.categories.length === 1 ? applied.categories[0] : null

	const isolateOffice = (code) => {
		handleApply({ ...applied, offices: code ? [code] : [] })
	}
	const isolateCategory = (sipp) => {
		handleApply({ ...applied, categories: sipp ? [sipp] : [] })
	}

	return (
		<MbPage title={MY_BUSINESS_ROUTES.FLEET_TITLE}
		        subtitle={MY_BUSINESS_ROUTES.FLEET_SUBTITLE}>
			<Box>
				<FiltersBar value={draft}
				            applied={applied}
				            onChange={setDraft}
				            onApply={handleApply}
				            loading={loading}
				            csvHref={`/api/my-business/fleet/download?${filtersToQuery(applied)}`}
				            csvLabel="Fleet CSV"/>


				{error && <AppNote severity="error" sx={{ mb: 2 }}>Backend unreachable: {error}</AppNote>}

				{pageLoading && <MbPageSkeleton kpis={4} blocks={['chart', 'chart', 'split', 'table']}/>}

				{data && !pageLoading && (
					<Box>
						<KpiBanner kpi={data.kpi}
						           compareKpi={data.compare?.kpi}
						           yoy={data.compare?.yoy}
						           activeMetricKey={activeMetricKey}
						           onSelectMetric={setActiveMetricKey}/>
						<Box sx={{ mt: 3 }}>
							<FleetChart reloading={chartReloading}
							            monthly={data.monthly}
							            compareMonthly={data.compare?.monthly}
							            compareLabel={data.compare && describeCompare(data.compare)}
							            metric={activeMetric}
							            granularity={data.granularity}
							            onGranularityChange={handleGranularity}/>
						</Box>
						<Box sx={{ mt: 3 }}>
							<FleetSizeVsBookedChart monthly={data.monthly}
							                        granularity={data.granularity}/>
						</Box>
						{/* View-mode toggle for the breakdowns (Thibault's 3 checkboxes for "Fleet by Category"). */}
						<Box justifyContent="space-between" sx={{ mt: 3, mb: 2, ...mbSectionBarSx }}>
							<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>
								Metric: <strong>{activeMetric.label}</strong> · pick another in the banner
							</Typography>
							<Box display="flex" alignItems="center" gap={1.5}>
								<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
									View
								</Typography>
								<ToggleButtonGroup size="small" exclusive value={viewMode}
								                   onChange={(_, v) => v && setViewMode(v)}>
									<ToggleButton value="raw">Raw</ToggleButton>
									<ToggleButton value="vs_py" disabled={!data.compare}>vs PY</ToggleButton>
									<ToggleButton value="per_unit" disabled={!data.compare}>per Unit</ToggleButton>
								</ToggleButtonGroup>
							</Box>
						</Box>

						<Grid container spacing={2} sx={{ mt: 0 }}>
							<Grid item xs={12} md={6}>
								<ShareList title={`Share by Office · ${activeMetric.label}`}
								           rows={applyViewMode(data.by_office, viewMode, data, activeMetric.key)}
								           labelOf={(r) => r.code}
								           subOf={(r)   => r.name || ''}
								           valueFormatter={activeMetric.format}
								           valueKey={activeMetric.key}
								           keyField="code"
								           selectedKey={selectedOffice}
								           onSelect={isolateOffice}/>
							</Grid>
							<Grid item xs={12} md={6}>
								<ShareList title={`Share by Category (SIPP) · ${activeMetric.label}`}
								           rows={applyViewMode(data.by_category, viewMode, data, activeMetric.key)}
								           labelOf={(r) => r.sipp_code}
								           subOf={(r)   => r.vehicle_class || ''}
								           valueFormatter={activeMetric.format}
								           valueKey={activeMetric.key}
								           keyField="sipp_code"
								           selectedKey={selectedCategory}
								           onSelect={isolateCategory}/>
							</Grid>
						</Grid>
						<Box sx={{ mt: 3 }}>
							<PeakCalendar offices={applied.offices}
							              categories={applied.categories}
							              from={applied.date.from}
							              to={applied.date.to}/>
						</Box>
						<Box sx={{ mt: 3 }}>
							<FleetCascadeMatrix offices={applied.offices}
							                    defaultDay={applied.date.from}/>
						</Box>
					</Box>
				)}
			</Box>
		</MbPage>
	)
}

function describeCompare(c) {
	if (c.mode === 'py') return 'Previous Year'
	return `${displayDay(c.from)} → ${displayDay(c.to)}`
}

// ---------------------------------------------------------------------------
// Fleet Size vs Booked — the POC's signature chart. Two series on the
// same axis: total fleet (top line, light area underneath) and booked
// (bottom line). The visual gap between them IS the available
// inventory — operators read saturation at a glance.
// ---------------------------------------------------------------------------
function FleetSizeVsBookedChart({ monthly, granularity }) {
	if (!monthly?.length) return null

	const periods    = monthly.map(m => m.month)
	const tickFormat = buildTickFormatter(periods)
	const tickNumber = pickTickNumber(periods)

	const fleetData  = monthly.map(m => m.avg_fleet  || 0)
	const bookedData = monthly.map(m => m.avg_booked || 0)
	const granLabel  = GRANULARITY_LABEL[granularity] || 'Monthly'

	const series = [
		{ id: 'Fleet', data: fleetData,  label: 'Fleet',  color: dsColorNew.textSubtle,
		  area: true, showMark: false, curve: 'monotoneX',
		  valueFormatter: fmtNum },
		{ id: 'Booked', data: bookedData, label: 'Booked', color: dataColors.red500,
		  area: true, showMark: false, curve: 'monotoneX',
		  valueFormatter: fmtNum },
	]

	const avgFleet  = fleetData.reduce((a, b) => a + b, 0)  / fleetData.length
	const avgBooked = bookedData.reduce((a, b) => a + b, 0) / bookedData.length
	const avgGap    = Math.max(0, avgFleet - avgBooked)

	return (
		<Paper sx={{ p: 2.5 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between"
			     flexWrap="wrap" gap={1.5} sx={{ mb: 1 }}>
				<AppCardTitle icon="chart-area" title={`${granLabel} Fleet Size vs Booked`}/>
				<Box display="flex" gap={2} sx={{ fontSize: 13, color: dsColorNew.textTitle }}>
					<Box>
						<Box component="span" sx={{ color: dsColorNew.textMuted }}>Avg Fleet </Box>
						<Box component="span" sx={{ fontWeight: 700 }}>{fmtNum(Math.round(avgFleet))}</Box>
					</Box>
					<Box>
						<Box component="span" sx={{ color: dsColorNew.textMuted }}>Avg Booked </Box>
						<Box component="span" sx={{ fontWeight: 700, color: dsColorNew.dangerRed }}>
							{fmtNum(Math.round(avgBooked))}
						</Box>
					</Box>
					<Box>
						<Box component="span" sx={{ color: dsColorNew.textMuted }}>Avg Idle </Box>
						<Box component="span" sx={{ fontWeight: 700 }}>{fmtNum(Math.round(avgGap))}</Box>
					</Box>
				</Box>
			</Box>
			<LineChart
				xAxis={[{ scaleType: 'point', data: periods,
				          valueFormatter: tickFormat, tickNumber }]}
				series={series}
				height={320}
				slotProps={mbLegendBottom}
				margin={{ top: 16, right: 16, bottom: 72, left: 56 }}
				sx={{
					'& .MuiAreaElement-series-Fleet':  { fillOpacity: 0.18 },
					'& .MuiAreaElement-series-Booked': { fillOpacity: 0.35 },
				}}/>
			<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, mt: 0.5 }}>
				The gap between the two lines is idle inventory — the
				closer they hug, the tighter you are on capacity.
			</Typography>
		</Paper>
	)
}

// ---------------------------------------------------------------------------
// KPI banner — same switcher pattern as Revenue
// ---------------------------------------------------------------------------
function KpiBanner({ kpi, compareKpi, yoy, activeMetricKey, onSelectMetric }) {
	const cards = [
		{ key: 'avg_fleet',     label: 'Avg Fleet',     format: fmtNum, icon: 'car' },
		{ key: 'avg_booked',    label: 'Avg Booked',    format: fmtNum, icon: 'calendar-check' },
		{ key: 'avg_available', label: 'Avg Available', format: fmtNum, icon: 'square-parking' },
		{ key: 'utilization',   label: 'Utilization',   format: fmtPct, icon: 'gauge-high' },
	]
	// Utilization card shows "47 / 95 cars" right below the percentage so
	// the absolute units sit next to the rate (Thibault feedback Jun-2026).
	const utilSubtext = (() => {
		const booked = Number(kpi?.avg_booked)
		const fleet  = Number(kpi?.avg_fleet)
		if (!Number.isFinite(booked) || !Number.isFinite(fleet) || fleet <= 0) return null
		return `${fmtNum(booked)} / ${fmtNum(fleet)} cars`
	})()
	return (
		<MbKpiRow>
			{cards.map((c) => (
				<MbKpiCard key={c.key}
				           icon={c.icon}
				           label={c.label}
				           value={c.format(kpi[c.key])}
				           compareValue={compareKpi ? c.format(compareKpi[c.key]) : null}
				           delta={compareKpi ? (yoy ? yoy[c.key] : null) : undefined}
				           hint={c.key === 'utilization' ? utilSubtext : null}
				           selected={c.key === activeMetricKey}
				           onSelect={() => onSelectMetric?.(c.key)}/>
			))}
		</MbKpiRow>
	)
}

// ---------------------------------------------------------------------------
// Time-series chart
// ---------------------------------------------------------------------------
function FleetChart({ reloading, monthly, compareMonthly, compareLabel, metric,
                     granularity, onGranularityChange }) {
	if (!monthly.length) {
		return (
			<Paper sx={{ p: 3 }}>
				<AppEmptyLine>No data in this date range.</AppEmptyLine>
			</Paper>
		)
	}

	const granLabel  = GRANULARITY_LABEL[granularity] || 'Monthly'
	const periods    = monthly.map(m => m.month)
	const tickFormat = buildTickFormatter(periods)
	const tickNumber = pickTickNumber(periods)

	const months = monthly.map(m => m.month)
	const values = monthly.map(m => m[metric.key] || 0)
	const series = [{ data: values, label: metric.chartTitle, valueFormatter: metric.format }]
	if (compareMonthly && compareMonthly.length) {
		const cmpValues = compareMonthly.map(m => m[metric.key] || 0)
		while (cmpValues.length < months.length) cmpValues.push(0)
		series.push({ data: cmpValues.slice(0, months.length),
		              label: compareLabel || 'Compare',
		              valueFormatter: metric.format })
	}

	return (
		<Paper sx={{ p: 2.5 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
				<AppCardTitle icon="chart-column" title={`${granLabel} ${metric.chartTitle}`}/>
				<AppPillToggle size="small" value={granularity || 'monthly'} onChange={(v) => onGranularityChange?.(v)} options={GRANULARITY_OPTIONS}/>
			</Box>
			{/* a reload swaps only the chart for the skeleton's chart block: the title and the toggle stay */}
			{reloading ? <Skeleton variant="rounded" height={340} sx={{ borderRadius: '10px' }}/> : granularity === 'daily' ? (
				<LineChart
					colors={MB_CHART_COLORS}
					xAxis={[{ scaleType: 'point', data: months, valueFormatter: tickFormat, tickNumber }]}
					series={series.map((s, i) => ({
						...s, showMark: false, curve: 'monotoneX',
						area: i === 0,
					}))}
					height={340}
					slotProps={mbLegendBottom}
					margin={{ top: 16, right: 16, bottom: 72, left: 56 }}/>
			) : (
				<BarChart
					colors={MB_CHART_COLORS}
					xAxis={[{ scaleType: 'band', data: months, valueFormatter: tickFormat, tickNumber }]}
					series={series}
					height={340}
					slotProps={mbLegendBottom}
					margin={{ top: 16, right: 16, bottom: 72, left: 56 }}/>
			)}
		</Paper>
	)
}


// ---------------------------------------------------------------------------
// View-mode transform for the breakdown shares (Thibault's 3 checkboxes).
// 'raw'      → strip compare so deltas disappear.
// 'vs_py'    → identity.
// 'per_unit' → scale absolute metrics by avg_fleet (period) / avg_fleet_py.
//              Utilization is already a percentage; leave it alone.
// ---------------------------------------------------------------------------
function applyViewMode(rows, mode, data, metricKey) {
	if (!rows) return rows
	if (mode === 'raw') {
		return rows.map(r => ({
			...r,
			revenue_compare: undefined,
			delta_pct: null,
		}))
	}
	if (mode === 'per_unit') {
		const avg   = Math.max(Number(data?.kpi?.avg_fleet) || 0, 1)
		const avgPy = Math.max(Number(data?.compare?.kpi?.avg_fleet) || 0, 1)
		const ratio = (k) => (k === 'utilization' ? null : true)  // skip util
		return rows.map(r => {
			const out = { ...r }
			for (const k of ['avg_fleet', 'avg_booked', 'avg_available']) {
				if (out[k] != null) out[k] = out[k] / avg
			}
			if (r.revenue_compare != null) {
				out.revenue_compare = metricKey === 'utilization'
					? r.revenue_compare
					: (r.revenue_compare || 0) / avgPy
				const cur = out[metricKey]
				const prev = out.revenue_compare
				out.delta_pct = (cur != null && prev) ? Number(((cur - prev) / prev * 100).toFixed(1)) : null
			}
			return out
		})
	}
	return rows
}
